import { theme } from "@shared/utils/themeColors";
import React, { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SettingsSidebar from "../../../partials/settings/SettingsSidebar";
import AuthenticatedLayout from "../../../shared/layouts/AuthenticatedLayout";
import { useQueryClient } from "@tanstack/react-query";

/**
 * Success page for subscription upgrade from monthly to yearly plan
 */
const SubscriptionUpgradeToYearlySuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const queryClient = useQueryClient();

  // Invalidate the subscription query to refresh the data
  useEffect(() => {
    if (sessionId) {
      queryClient.invalidateQueries({ queryKey: ["currentSubscription"] });
    }
  }, [sessionId, queryClient]);

  return (
    <AuthenticatedLayout>
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        {/* Page header */}
        <div className="mb-8">
          {/* Title */}
          <h1
            className={`text-2xl md:text-3xl ${theme.text.primary} font-bold`}
          >
            Account Settings
          </h1>
        </div>

        {/* Content */}
        <div className={`${theme.bg.primary} shadow-sm rounded-xl mb-8`}>
          <div className="flex flex-col md:flex-row md:-mr-px">
            <SettingsSidebar />
            <div className="grow">
              <div className="p-6">
                <div className="max-w-md mx-auto">
                  <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 bg-green-100 dark:bg-green-900/30 rounded-full">
                    <svg
                      className="w-8 h-8 text-green-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>

                  <h2
                    className={`text-2xl font-bold text-center ${theme.text.primary} mb-4`}
                  >
                    Yearly Plan Upgrade Complete
                  </h2>

                  <p className="text-center text-gray-600 dark:text-gray-300 mb-6">
                    Your subscription has been successfully upgraded to the yearly plan. You now have access to all the benefits of your yearly subscription. Thank you for your continued support!
                  </p>

                  <div className="text-center">
                    <Link
                      to="/settings/plans"
                      className="inline-block py-2 px-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-colors"
                    >
                      Return to Subscription Settings
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  );
};

export default SubscriptionUpgradeToYearlySuccessPage;
